/**
 * Utility functions to help with Firebase Storage image access issues
 */

export interface ImageAccessInfo {
  isAccessible: boolean;
  errorType?: 'cors' | 'network' | 'permission' | 'unknown';
  statusCode?: number;
  message?: string;
}

/**
 * Test if an image URL is accessible and diagnose common issues
 */
export const testImageAccess = async (imageUrl: string): Promise<ImageAccessInfo> => {
  if (!imageUrl) {
    return { isAccessible: false, errorType: 'unknown', message: 'No URL provided' };
  }

  try {
    // First try a simple fetch to check if URL is reachable
    const response = await fetch(imageUrl, { 
      method: 'HEAD',
      mode: 'cors' // This will fail if CORS is not configured
    });
    
    if (response.ok) {
      return { isAccessible: true };
    } else {
      return {
        isAccessible: false,
        errorType: 'permission',
        statusCode: response.status,
        message: `HTTP ${response.status} - ${response.statusText}`
      };
    }
  } catch (error: any) {
    // Analyze the error to determine the likely cause
    if (error.name === 'TypeError' && error.message.includes('CORS')) {
      return {
        isAccessible: false,
        errorType: 'cors',
        message: 'CORS policy prevents access to this image'
      };
    } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      // Try with no-cors to see if it's a network vs CORS issue
      try {
        await fetch(imageUrl, { method: 'HEAD', mode: 'no-cors' });
        return {
          isAccessible: false,
          errorType: 'cors',
          message: 'Image URL is reachable but CORS is not configured'
        };
      } catch (noCorsError) {
        return {
          isAccessible: false,
          errorType: 'network',
          message: 'Network error or URL not reachable'
        };
      }
    } else {
      return {
        isAccessible: false,
        errorType: 'unknown',
        message: error.message || 'Unknown error occurred'
      };
    }
  }
};

/**
 * Convert Firebase Storage gs:// URL to public HTTP URL
 * This helps handle cases where the URL format is incorrect
 */
export const convertFirebaseStorageUrl = (url: string): string => {
  if (!url) return '';
  
  // If it's already an HTTP URL, return as-is
  if (url.startsWith('https://')) {
    return url;
  }
  
  // If it's a gs:// URL, convert it to public HTTP URL
  if (url.startsWith('gs://')) {
    // Extract bucket and path from gs://bucket/path
    const match = url.match(/^gs:\/\/([^\/]+)\/(.+)$/);
    if (match) {
      const [, bucket, path] = match;
      return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
    }
  }
  
  return url;
};

/**
 * Create a fallback image URL or data URI
 * This can be used when the original image fails to load
 */
export const createFallbackAvatarUrl = (name: string): string => {
  const initials = name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Create a simple SVG avatar with initials
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="50" fill="#1976d2"/>
      <text x="50" y="50" dy="0.35em" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="36" font-weight="bold">
        ${initials}
      </text>
    </svg>
  `;
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Log detailed information about image access for debugging
 */
export const logImageAccessDiagnostics = async (imageUrl: string, context: string) => {
  if (process.env.NODE_ENV !== 'development') return;
  
  console.group(`[ImageAccess] Diagnosing image access for ${context}`);
  console.log('URL:', imageUrl);
  console.log('User Agent:', navigator.userAgent);
  console.log('Current Domain:', window.location.hostname);
  console.log('Protocol:', window.location.protocol);
  
  const accessInfo = await testImageAccess(imageUrl);
  console.log('Access Test Result:', accessInfo);
  
  if (!accessInfo.isAccessible) {
    console.warn(`Image access failed: ${accessInfo.errorType} - ${accessInfo.message}`);
    
    // Provide specific guidance based on error type
    switch (accessInfo.errorType) {
      case 'cors':
        console.info('💡 Solution: Configure CORS in Firebase Storage or update Firebase Storage rules');
        break;
      case 'permission':
        console.info('💡 Solution: Update Firebase Storage rules to allow public read access');
        break;
      case 'network':
        console.info('💡 Solution: Check network connectivity or DNS resolution');
        break;
      default:
        console.info('💡 Solution: Check Firebase configuration and URL format');
    }
  }
  
  console.groupEnd();
};